import { useEffect, useState } from "react";
import { ShieldAlert, ShieldCheck, ShieldQuestion, ChevronDown, ChevronRight } from "lucide-react";
import api from "../../lib/api";

const fmtMoney = (v) =>
  v == null ? "—" : `$${Math.round(Number(v)).toLocaleString()}`;
const fmtRatio = (v) => (v == null ? "—" : `${Number(v).toFixed(2)}x`);

const STATUS_TONE = {
  underfunded: {
    border: "border-red-300",
    bg: "bg-red-50",
    text: "text-red-900",
    pillBg: "bg-red-600",
    icon: ShieldAlert,
    label: "Underfunded",
  },
  tight: {
    border: "border-amber-300",
    bg: "bg-amber-50",
    text: "text-amber-900",
    pillBg: "bg-amber-600",
    icon: ShieldQuestion,
    label: "Tight",
  },
  covered: {
    border: "border-emerald-300",
    bg: "bg-emerald-50",
    text: "text-emerald-900",
    pillBg: "bg-emerald-600",
    icon: ShieldCheck,
    label: "Covered",
  },
  no_ti: {
    border: "border-slate-200",
    bg: "bg-slate-50",
    text: "text-slate-700",
    pillBg: "bg-slate-500",
    icon: ShieldQuestion,
    label: "No T&I set",
  },
};

/**
 * Tax + Insurance coverage banner shown above the SREO table. Surfaces
 * properties where annual taxes + insurance can't be funded from cash flow
 * after debt service — exactly the analysis a CPA runs during portfolio
 * review.
 */
export default function TaxInsuranceCoverageBanner() {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    api.get("/reports/ti-coverage").then((r) => setData(r.data)).catch(() => setData(null));
  }, []);

  if (!data || !data.rows || data.rows.length === 0) return null;

  const portfolio = data.portfolio || {};
  const underfunded = data.rows.filter((r) => r.status === "underfunded");
  const tight = data.rows.filter((r) => r.status === "tight");
  const flagged = [...underfunded, ...tight];

  // Nothing to flag — render a low-key "all covered" pill
  if (flagged.length === 0) {
    return (
      <div
        className="border border-emerald-200 bg-emerald-50 text-emerald-900 rounded-sm p-3 mb-4 flex items-center gap-2 text-xs"
        data-testid="ti-coverage-banner"
      >
        <ShieldCheck className="w-4 h-4 shrink-0" />
        <span>
          <strong>Tax & insurance coverage healthy.</strong> All {data.rows.length} properties generate enough cash flow after debt service to cover their next T&I obligation.
          {portfolio.portfolio_coverage_ratio != null && (
            <span className="text-emerald-700"> · Portfolio coverage ratio: {fmtRatio(portfolio.portfolio_coverage_ratio)}</span>
          )}
        </span>
      </div>
    );
  }

  const headTone = underfunded.length > 0 ? STATUS_TONE.underfunded : STATUS_TONE.tight;
  const HeadIcon = headTone.icon;
  const totalShortfall = underfunded.reduce((s, r) => s + (r.shortfall || 0), 0);

  return (
    <div
      className={`border ${headTone.border} ${headTone.bg} ${headTone.text} rounded-sm mb-4`}
      data-testid="ti-coverage-banner"
    >
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left"
        data-testid="ti-coverage-toggle"
      >
        <div className="flex items-start gap-3">
          <HeadIcon className="w-5 h-5 shrink-0 mt-0.5" />
          <div>
            <div className="font-bold text-sm">
              {underfunded.length > 0 && (
                <span data-testid="ti-coverage-underfunded-count">
                  {underfunded.length} {underfunded.length === 1 ? "property" : "properties"} underfunded
                </span>
              )}
              {underfunded.length > 0 && tight.length > 0 && <span className="text-slate-500"> · </span>}
              {tight.length > 0 && (
                <span data-testid="ti-coverage-tight-count">
                  {tight.length} tight
                </span>
              )}
            </div>
            <div className="text-xs mt-0.5 opacity-90">
              {underfunded.length > 0 ? (
                <>
                  Cash flow after debt service can't cover the next tax bill + insurance premium.
                  {totalShortfall > 0 && (
                    <strong> Total shortfall: {fmtMoney(totalShortfall)}/yr.</strong>
                  )}
                </>
              ) : (
                <>
                  Coverage ratio is between 1.0x and 1.5x — minor unexpected expenses could leave T&I underfunded.
                </>
              )}
            </div>
          </div>
        </div>
        {expanded ? <ChevronDown className="w-4 h-4 shrink-0" /> : <ChevronRight className="w-4 h-4 shrink-0" />}
      </button>

      {expanded && (
        <div className="border-t border-current/10 px-4 py-3" data-testid="ti-coverage-detail">
          <table className="w-full text-xs font-mono">
            <thead>
              <tr className="text-left">
                <th className="pb-2 overline opacity-60 font-sans">Property</th>
                <th className="pb-2 overline opacity-60 font-sans text-right">T&I / yr</th>
                <th className="pb-2 overline opacity-60 font-sans text-right">Cash flow</th>
                <th className="pb-2 overline opacity-60 font-sans text-right">Coverage</th>
                <th className="pb-2 overline opacity-60 font-sans text-right">Shortfall</th>
                <th className="pb-2 overline opacity-60 font-sans text-right">Status</th>
              </tr>
            </thead>
            <tbody>
              {flagged.map((r) => {
                const tone = STATUS_TONE[r.status];
                return (
                  <tr
                    key={r.property_id}
                    className="border-t border-current/10"
                    data-testid={`ti-coverage-row-${r.property_id}`}
                  >
                    <td className="py-1.5 font-sans font-semibold">{r.name}</td>
                    <td className="py-1.5 text-right">{fmtMoney(r.annual_ti_total)}</td>
                    <td className="py-1.5 text-right">{fmtMoney(r.cash_flow_after_debt)}</td>
                    <td className="py-1.5 text-right font-bold">{fmtRatio(r.coverage_ratio)}</td>
                    <td className="py-1.5 text-right">
                      {r.shortfall > 0 ? <strong>{fmtMoney(r.shortfall)}</strong> : "—"}
                    </td>
                    <td className="py-1.5 text-right">
                      <span
                        className={`inline-block px-2 py-0.5 ${tone.pillBg} text-white rounded-sm uppercase tracking-wider font-sans font-bold text-[10px]`}
                      >
                        {tone.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="text-[11px] mt-3 opacity-75 font-sans leading-relaxed">
            <strong>Coverage ratio</strong> = (NOI − annual debt service) / (annual taxes + insurance).
            Healthy ≥ 1.5x · Tight 1.0x–1.5x · Underfunded &lt; 1.0x. Properties without T&I configured don't appear here — set them on the property's edit form.
          </div>
        </div>
      )}
    </div>
  );
}
